import { useState, useEffect, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAnnouncements, useUsersCount } from '../../hooks/useApi'
import { useAppStore } from '../../store/useAppStore'
import type { AnnouncementWithCard } from '../../types/api'
import { FEATURES } from '@/config/features.ts'
import './RecipeOfTheDay.css'
import logo from '../../assets/images/logo.svg'
import avatarPlaceholder from '../../assets/images/avatar.png'
import Claude from '../../assets/images/cloud.svg'
import logoLight from '../../assets/images/logoLight.svg'
import wheelBgPattern from '../../assets/images/wheelBanner/Group 48096225.svg'
import flavorWheel from '../../assets/images/flavorWheelBanner.png'

interface CustomBanner {
  isCustomBanner: true
  id: string
  bannerType?: 'subscription' | 'fortune-wheel' | 'flavor-constructor'
}

/** Баннер вкусового конструктора — показываем всем, ведёт на /flavors. */
const FLAVOR_BANNER: CustomBanner = {
  isCustomBanner: true,
  id: 'flavor-constructor-banner',
  bannerType: 'flavor-constructor',
}

type SlideItem = AnnouncementWithCard | CustomBanner

const RecipeOfTheDay = ({ hasSubscription }: { hasSubscription: boolean }) => {
  const navigate = useNavigate()
  const { data: announcements, isLoading } = useAnnouncements()
  const { data: usersCount } = useUsersCount()
  const { user } = useAppStore()
  const [currentSlide, setCurrentSlide] = useState(0)
  const [touchStart, setTouchStart] = useState<number | null>(null)
  const [touchEnd, setTouchEnd] = useState<number | null>(null)
  const [isAutoPlaying, setIsAutoPlaying] = useState(true)
  // Стрелки показываем только там, где нет тача (на мобиле листают свайпом).
  const isDesktop = !('ontouchstart' in window) || window.innerWidth > 768
  const intervalRef = useRef<number | null>(null)
  const resumeTimeoutRef = useRef<number | null>(null)

  // Формируем список слайдов с учетом подписки
  const serverSlides: AnnouncementWithCard[] =
    announcements?.announcements || []
  const allSlides: SlideItem[] = hasSubscription
    ? [
        ...(FEATURES.fortuneWheel
          ? [
              {
                isCustomBanner: true,
                id: 'fortune-wheel-banner',
                bannerType: 'fortune-wheel',
              } as CustomBanner,
            ]
          : []),
        FLAVOR_BANNER,
        ...serverSlides,
      ]
    : [
        // Кастомный баннер для пользователей без подписки
        {
          isCustomBanner: true,
          id: 'subscription-banner',
          bannerType: 'subscription',
        } as CustomBanner,
        FLAVOR_BANNER,
        ...serverSlides,
      ]

  // Единый useEffect для управления автопроигрыванием
  useEffect(() => {
    // Очистка предыдущих таймеров
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
    if (resumeTimeoutRef.current) {
      clearTimeout(resumeTimeoutRef.current)
      resumeTimeoutRef.current = null
    }

    if (allSlides.length > 1) {
      if (isAutoPlaying) {
        // Запускаем автопроигрывание
        intervalRef.current = setInterval(() => {
          setCurrentSlide(prev => (prev + 1) % allSlides.length)
        }, 5000)
      } else {
        // Возобновляем автопроигрывание через 10 секунд
        resumeTimeoutRef.current = setTimeout(() => {
          setIsAutoPlaying(true)
        }, 10000)
      }
    }

    // Очистка при размонтировании или изменении зависимостей
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
      if (resumeTimeoutRef.current) {
        clearTimeout(resumeTimeoutRef.current)
        resumeTimeoutRef.current = null
      }
    }
  }, [isAutoPlaying, allSlides.length])

  if (isLoading || allSlides.length === 0) {
    return (
      <section className="recipe-of-the-day">
        <div className="banner banner--skeleton">
          <div className="banner-header">
            <div className="banner-header-left">
              <img src={logo} alt="Pauline Cake" className="banner-logo" />
              {usersCount && (
                <p className="banner-users">
                  {usersCount.users_count.toLocaleString()} пользователя
                </p>
              )}
            </div>
            <Link to="/profile" className="banner-avatar">
              <img
                src={user?.photo_url || avatarPlaceholder}
                alt={user?.first_name || 'Профиль'}
              />
            </Link>
          </div>

          <div className="banner-slide-content">
            <div className="banner-skeleton-kicker" />
            <div className="banner-skeleton-title" />
          </div>
        </div>
      </section>
    )
  }

  const nextSlide = () => {
    setCurrentSlide(prev => (prev + 1) % allSlides.length)
  }

  const prevSlide = () => {
    setCurrentSlide(prev => (prev - 1 + allSlides.length) % allSlides.length)
  }

  const goToSlide = (index: number) => {
    setCurrentSlide(index)
    setIsAutoPlaying(false) // Останавливаем автопроигрывание
  }

  const pauseAutoPlay = () => {
    setIsAutoPlaying(false)
  }

  const resumeAutoPlay = () => {
    if (allSlides.length > 1) {
      setIsAutoPlaying(true)
    }
  }

  // Нет модального окна: при клике на кастомный баннер переходим на страницу подписки

  const handlePrevClick = () => {
    prevSlide()
    setIsAutoPlaying(false) // Останавливаем автопроигрывание
  }

  const handleNextClick = () => {
    nextSlide()
    setIsAutoPlaying(false) // Останавливаем автопроигрывание
  }

  // Минимальное расстояние для распознавания свайпа
  const minSwipeDistance = 50

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null)
    setTouchStart(e.targetTouches[0].clientX)
    setIsAutoPlaying(false) // Останавливаем автопроигрывание при touch
  }

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX)
  }

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return

    const distance = touchStart - touchEnd
    const isLeftSwipe = distance > minSwipeDistance
    const isRightSwipe = distance < -minSwipeDistance

    if (isLeftSwipe) {
      nextSlide()
    } else if (isRightSwipe) {
      prevSlide()
    }
  }

  return (
    <section className="recipe-of-the-day">
      <div
        className="banner"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onMouseEnter={pauseAutoPlay}
        onMouseLeave={resumeAutoPlay}
      >
        {/* Стрелки листания для десктопа — приглушённые, ярче при наведении */}
        {isDesktop && allSlides.length > 1 && (
          <>
            <button
              className="banner-nav banner-nav--prev"
              onClick={handlePrevClick}
              aria-label="Предыдущий слайд"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path
                  d="M15 18L9 12L15 6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <button
              className="banner-nav banner-nav--next"
              onClick={handleNextClick}
              aria-label="Следующий слайд"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path
                  d="M9 18L15 12L9 6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </>
        )}

        <div
          className="banner-slides"
          style={{
            transform: `translateX(-${currentSlide * (100 / allSlides.length)}%)`,
            width: `${allSlides.length * 100}%`,
          }}
        >
          {allSlides.map(slide => {
            // Кастомный баннер
            if ('isCustomBanner' in slide && slide.isCustomBanner) {
              const customSlide = slide as CustomBanner

              // Баннер вкусового конструктора
              if (customSlide.bannerType === 'flavor-constructor') {
                return (
                  <div
                    key={slide.id}
                    className="banner-slide banner-slide--flavor"
                    onClick={() => navigate('/flavors')}
                    style={{
                      width: `${100 / allSlides.length}%`,
                    }}
                  >
                    <img
                      src={flavorWheel}
                      alt=""
                      className="banner-flavor-wheel"
                    />
                    <div className="banner-slide-content">
                      <div className="banner-slide-textgroup">
                        <span className="banner-slide-badge">
                          Вкусовой конструктор
                        </span>
                        <h2 className="banner-slide-title">
                          Собери
                          <br />
                          свой вкус
                        </h2>
                        <p className="banner-slide-text">
                          349 проверенных сочетаний из «Тезауруса вкусов» – от
                          классики до самых смелых
                        </p>
                      </div>
                      <span className="banner-slide-cta">Подробнее →</span>
                    </div>
                  </div>
                )
              }

              // Баннер колеса фортуны для пользователей с подпиской
              if (customSlide.bannerType === 'fortune-wheel') {
                return (
                  <div
                    key={slide.id}
                    className="banner-slide banner-slide--wheel"
                    onClick={() =>
                      navigate('/fortune-wheel', {
                        state: { showLoader: true },
                      })
                    }
                    style={{
                      width: `${100 / allSlides.length}%`,
                    }}
                  >
                    <img
                      src={wheelBgPattern}
                      alt=""
                      className="banner-slide-pattern"
                    />
                    <div className="banner-slide-content">
                      <p className="banner-slide-kicker">Розыгрыш</p>
                      <h2 className="banner-slide-title">
                        Праздничный розыгрыш
                      </h2>
                      <p className="banner-slide-text">
                        Крути колесо и выигрывай ценные призы
                      </p>
                      <span className="banner-slide-cta">
                        Подробнее →
                      </span>
                    </div>
                  </div>
                )
              }

              // Баннер подписки для пользователей без подписки
              return (
                <div
                  key={slide.id}
                  className="banner-slide banner-slide--subscription"
                  onClick={() => navigate('/subscribe')}
                  style={{
                    width: `${100 / allSlides.length}%`,
                  }}
                >
                  <img src={Claude} alt="" className="claude claude01" />
                  <img src={Claude} alt="" className="claude claude02" />
                  <img src={Claude} alt="" className="claude claude03" />
                  <div className="banner-slide-content">
                    <p className="banner-slide-kicker">Подписка</p>
                    <img
                      src={logoLight}
                      alt="Secret Chat"
                      className="banner-slide-logo"
                    />
                    <p className="banner-slide-text">
                      Получи доступ к 300+ рецептам и обучающим материалам
                    </p>
                    <span className="banner-slide-cta">
                      Подробнее →
                    </span>
                  </div>
                </div>
              )
            }

            // Обычные слайды с сервера
            const announcement = slide as AnnouncementWithCard

            // Определяем, куда ведет слайд
            const slideContent = (
              <>
                <img
                  className="banner-slide-image"
                  src={announcement.banner_url}
                  alt=""
                />
                <div className="banner-slide-scrim" />
                <div className="banner-slide-content">
                  <p className="banner-slide-kicker">
                    {announcement.announcement_text}
                  </p>
                  <h2 className="banner-slide-title">
                    {announcement.announcement_subtitle}
                  </h2>
                  <span className="banner-slide-cta">
                    Подробнее →
                  </span>
                </div>
              </>
            )

            // Если есть link_url, то это внешняя ссылка
            if (announcement.link_url) {
              return (
                <a
                  key={
                    announcement.card_id || `link-${announcement.sort_order}`
                  }
                  href={announcement.link_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="banner-slide"
                  style={{
                    width: `${100 / allSlides.length}%`,
                  }}
                >
                  {slideContent}
                </a>
              )
            }

            // Если есть card_id, то это ссылка на карточку
            if (announcement.card_id) {
              return (
                <Link
                  key={announcement.card_id}
                  to={`/card/${announcement.card_id}`}
                  className="banner-slide"
                  style={{
                    width: `${100 / allSlides.length}%`,
                  }}
                >
                  {slideContent}
                </Link>
              )
            }

            // Если нет ни link_url, ни card_id, то ведем на страницу подписки
            return (
              <Link
                key={`announcement-${announcement.sort_order}`}
                to="/subscribe"
                className="banner-slide"
                style={{
                  width: `${100 / allSlides.length}%`,
                }}
              >
                {slideContent}
              </Link>
            )
          })}
        </div>

        {/* Шапка поверх баннера: логотип, счётчик пользователей, аватар.
            На цветных слайдах (подписка/розыгрыш) — светлый вариант. */}
        <div
          className={`banner-header ${
            'isCustomBanner' in (allSlides[currentSlide] ?? {})
              ? 'banner-header--inverse'
              : ''
          }`}
        >
          <div className="banner-header-left">
            <img src={logo} alt="Pauline Cake" className="banner-logo" />
            {usersCount && (
              <p className="banner-users">
                {usersCount.users_count.toLocaleString()} пользователя
              </p>
            )}
          </div>
          <Link to="/profile" className="banner-avatar">
            <img
              src={user?.photo_url || avatarPlaceholder}
              alt={user?.first_name || 'Профиль'}
            />
          </Link>
        </div>

        {/* Прогресс слайдов: активный заполняется за время автопрокрутки */}
        {allSlides.length > 1 && (
          <div className="banner-progress">
            {allSlides.map((_, index) => (
              <button
                key={index}
                className={`banner-progress-item ${
                  index === currentSlide ? 'active' : ''
                }`}
                onClick={() => goToSlide(index)}
                aria-label={`Перейти к слайду ${index + 1}`}
              >
                {index === currentSlide && (
                  <span
                    key={currentSlide}
                    className="banner-progress-fill"
                    style={{
                      animationPlayState: isAutoPlaying ? 'running' : 'paused',
                    }}
                  />
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Клик по кастомному баннеру ведет на /subscribe */}
    </section>
  )
}

export default RecipeOfTheDay
